import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listTransfers, createTransfer, deleteTransfer } from "@/lib/transfers.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, ArrowRightLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { DateFieldSelect } from "@/components/filters/date-field-select";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const transfersSearchSchema = dateRangeSearchSchema.extend({
  dateField: fallback(z.enum(["created_at", "posted_at"]).optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/transfers/")({
  validateSearch: zodValidator(transfersSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: TransfersPage,
});

function TransfersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  const navigateSearch = useNavigate({ from: Route.fullPath });
  const dateField = (urlSearch.dateField ?? "created_at") as "created_at" | "posted_at";
  const sort = urlSearch.sort ?? "desc";
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: urlSearch.start, end: urlSearch.end }),
    [urlSearch.start, urlSearch.end],
  );
  const { tenantId, facilityId, customerId, facilities } = useScope();
  const listTransfersFn = useServerFn(listTransfers);
  const createTransferFn = useServerFn(createTransfer);
  const deleteTransferFn = useServerFn(deleteTransfer);

  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [destFacility, setDestFacility] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["stock-transfers", tenantId, facilityId, customerId, fromIso, toIso, dateField, sort],
    enabled: !!tenantId,
    queryFn: () => listTransfersFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, fromIso, toIso, dateField, sort } }),
  });

  const tenantFacilities = facilities.filter((f) => f.tenant_id === tenantId);
  const facMap = Object.fromEntries(tenantFacilities.map((f) => [f.id, f.name]));

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId || !destFacility) return;
    setSubmitting(true);
    try {
      const res = await runServerFn(
        () => createTransferFn({ data: {
          tenantId, sourceFacilityId: facilityId, destFacilityId: destFacility,
          reference: reference.trim() || null, notes: notes.trim() || null,
        } }),
        "Create transfer",
      );
      toast.success("Transfer created");
      setOpen(false); setReference(""); setNotes(""); setDestFacility("");
      qc.invalidateQueries({ queryKey: ["stock-transfers", tenantId] });
      navigate({ to: "/app/transfers/$id", params: { id: res.id } });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const remove = async (e: React.MouseEvent, rowId: string) => {
    e.stopPropagation();
    if (!confirm("Delete this draft transfer? Lines will be removed.")) return;
    try {
      await runServerFn(() => deleteTransferFn({ data: { id: rowId } }), "Delete transfer");
      toast.success("Transfer deleted");
      qc.invalidateQueries({ queryKey: ["stock-transfers", tenantId] });
    } catch { /* toast handled */ }
  };

  return (
    <>
      <PageHeader
        title="Stock Transfers"
        description="Move inventory between locations or facilities."
        actions={
          <div className="flex items-center gap-2">
          <HelpButton moduleKey="transfers" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={!tenantId || !facilityId}><Plus className="h-4 w-4 mr-2" />New transfer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create stock transfer</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-2">
                  <Label>Destination facility</Label>
                  <Select value={destFacility} onValueChange={setDestFacility}>
                    <SelectTrigger><SelectValue placeholder="Select facility" /></SelectTrigger>
                    <SelectContent>
                      {tenantFacilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}{f.id === facilityId ? " (same as source)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Source: {tenantFacilities.find((f) => f.id === facilityId)?.name ?? "—"}</p>
                </div>
                <div className="space-y-2"><Label>Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TR-2026-001" /></div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                <DialogFooter><Button type="submit" disabled={submitting || !destFacility}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        <div className="mb-4"><ContextualTip tipKey="transfers_intro" /></div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <DateFieldSelect
            value={dateField}
            onChange={(v) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, dateField: v === "created_at" ? undefined : (v as typeof urlSearch.dateField) }) })}
            options={[
              { value: "created_at", label: "Created" },
              { value: "posted_at", label: "Posted" },
            ]}
          />
          <DateRangeFilter
            value={{ start: urlSearch.start, end: urlSearch.end }}
            onChange={(r) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigateSearch({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
        </div>
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows && rows.length > 0 ? rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/app/transfers/$id", params: { id: r.id } })}>
                  <TableCell className="font-medium flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-muted-foreground" />{r.reference ?? r.id.slice(0, 8)}</TableCell>
                  <TableCell className="text-sm">{facMap[r.source_facility_id] ?? "—"} → {facMap[r.dest_facility_id] ?? "—"}</TableCell>
                  <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {r.status === "draft" && (
                      <Button variant="ghost" size="icon" onClick={(e) => remove(e, r.id)} aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground inline ml-1" />
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No transfers yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}